import { Component, EventEmitter, OnInit, Output, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ContactUsService } from '../core/services/contact-us.service';
import { LanguageService } from '../core/services/language.service';
import { IDictionaryCommon } from '../core/models/dictionary.model';

@Component({
  selector: 'app-become-appointed',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './become-appointed.component.html',
  styleUrl: './become-appointed.component.css'
})
export class BecomeAppointedComponent implements OnInit {
  @Output()
  formReady = new EventEmitter<FormGroup>();

  form: FormGroup;
  dropdownOpen = false;

  constructor(private fb: FormBuilder, private contactUsService: ContactUsService, private languageService: LanguageService) {}

  ngOnInit() {

    const lang = this.languageService.getLanguageFromUrl();

    this.form = this.fb.group({
      productCategory: [[] as string[]],
      productCategoryId: [[] as string[], [this.atLeastOneRequired.bind(this)]],
      otherInsurance: [''],
      region: ['US'],
      description: ['', [Validators.required, Validators.minLength(20)]]
    });

    this.form.get('productCategoryId')?.valueChanges.subscribe((values: string[]) => {
      const names = values.map(id => {
        const item = this.insuranceTypes.find(t => t.ItemID === id);
        return item ? item.DisplayName : id;
      });
      this.form.get('productCategory')?.setValue(names, { emitEvent: false });
    });

    this.formReady.emit(this.form);

    this.contactUsService.getProductCategories(lang).subscribe(data => {
      const sorted = [...data.filter(t => t.DisplayName !== 'Supplemental Health')].sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
      this.insuranceTypes = [...sorted, { ItemID: 'other', DisplayName: this.placeholders.other }];
      this.loadingInsuranceTypes = false;
    });

    this.contactUsService.getDictionary(lang).subscribe(() => {
      this.labels = this.contactUsService.commonLabels;
    });
  }

  atLeastOneRequired(control: AbstractControl): ValidationErrors | null {
    if (this.isOtherSelected) return null;
    const value = control.value;
    return Array.isArray(value) && value.length > 0 ? null : { required: true };
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  isSelected(itemId: string): boolean {
    if (itemId === 'other') return this.isOtherSelected;
    const values: string[] = this.form.get('productCategoryId')?.value || [];
    return values.includes(itemId);
  }

  isOtherSelected = false;

  toggleSelection(itemId: string): void {
    const control = this.form.get('productCategoryId');
    const otherInsurance = this.form.get('otherInsurance');
    if (itemId === 'other') {
      this.isOtherSelected = !this.isOtherSelected;
      if (this.isOtherSelected) {
        control?.setValue([] as string[]);
        const otherItem = this.insuranceTypes.find(t => t.ItemID === 'other');
        this.form.get('productCategory')?.setValue(otherItem ? otherItem.DisplayName : 'Other', { emitEvent: false });
        otherInsurance?.setValidators([Validators.required]);
      } else {
        this.form.get('productCategory')?.setValue([] as string[], { emitEvent: false });
        otherInsurance?.clearValidators();
        otherInsurance?.setValue('');
      }
      control?.updateValueAndValidity();
      otherInsurance?.updateValueAndValidity();
      this.dropdownOpen = false;
    } else {
      this.isOtherSelected = false;
      otherInsurance?.clearValidators();
      otherInsurance?.setValue('');
      otherInsurance?.updateValueAndValidity();
      const current: string[] = control?.value || [];
      if (current.includes(itemId)) {
        control?.setValue(current.filter(id => id !== itemId));
      } else {
        control?.setValue([...current, itemId]);
      }
    }
    control?.markAsTouched();
  }

  getSelectedDisplay(): string {
    if (this.isOtherSelected) {
      const otherItem = this.insuranceTypes.find(t => t.ItemID === 'other');
      return otherItem ? otherItem.DisplayName : 'Other';
    }
    const values: string[] = this.form.get('productCategoryId')?.value || [];
    if (values.length === 0) {
      return this.loadingInsuranceTypes ? 'Loading...' : this.placeholders.selectProductCat;
    }
    return values.length + ' selected';
  }

  getSelectedItems(): string[] {
    const values: string[] = this.form.get('productCategoryId')?.value || [];
    return values.map((id: string) => {
      const item = this.insuranceTypes.find(t => t.ItemID === id);
      return item ? item.DisplayName : id;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.multi-select-container')) {
      this.dropdownOpen = false;
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  insuranceTypes: any[] = [];

  loadingInsuranceTypes = true;

  labels: IDictionaryCommon = {} as IDictionaryCommon;

  get placeholders() { return this.contactUsService.placeholderTextLabels; }
  get messages() { return this.contactUsService.messageLabels; }
}
